import React, { createContext, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import API from '../../../../services/API'
import {
  GlobalContext,
  TenantSettingsContext,
  UserContext,
} from '../../../../App'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import PersonalDetails from './PersonalDetails'
import Address from './Address'
import ContactInfo from './ContactInfo'
import NextOfKin from './NextOfKin'
import ProfessionalQuals from './ProfessionalQuals'
import DrivingLicence from './DrivingLicence'
import UserAccount from './UserAccount'
import ErrorModal from '../../../../components/Modals/Error'
import AnDateField from '../../../../components/DateTime/DateField'
import ControlledDrugs from './ControlledDrugs'
import UpgradeRequired from '../../../../components/Upgrade/UpgradeGate'
import EmployeeBaseLocationsSelect from '../../../../components/EmployeeBaseLocationsSelect'
import planAllowances from '../../../../helpers/planAllowances.json'

// Figures come from the plan catalogue mirror, never typed in here.
const premiumEmployees = planAllowances.plans.premium.allowances.employees
const enterpriseEmployees = planAllowances.plans.enterprise.allowances.employees

export const EmployeeFormContext = createContext()

const EmployeeForm = ({
  isSelf,
  isNew,
  employeeID,
  onClose,
  onImageChange,
}) => {
  const [loading, setLoading] = useState(true)
  const [limitInfo, setLimitInfo] = useState({})
  const [formValues, setFormValues] = useState({})
  const navigate = useNavigate()

  const { setToast, getUserAccount, tenantInfo } = useContext(GlobalContext)
  const { subscriptionTier } = tenantInfo ?? {}
  const limitReached = limitInfo.used >= limitInfo.limit

  const [emailDup, setEmailDup] = useState(false)
  const [emailDupPopup, setEmailDupPopup] = useState(false)

  const [pinNumberDuplicate, setPinNumberDuplicate] = useState(false)
  const [baseLocationError, setBaseLocationError] = useState(null)

  const { user } = useContext(UserContext)

  const notAllowed =
    !user?.security?.isSiteAdmin && !user.employee?.teams.includes('Personnel')

  useEffect(() => {
    API.get('/subscription/usage').then((res) => {
      if (res.status === 200) {
        const { used, limit } = res.data.employees
        setLimitInfo({ used, limit })
      }
    })
    if (notAllowed) {
      setLoading(false)
      return
    }
    if (employeeID) {
      API.get('/employee/' + employeeID)
        .then((res) => {
          if (res.status === 200) {
            setFormValues(res.data)
          }
        })
        .catch((e) => console.error(e))
        .finally(() => setLoading(false))
    } else if (isSelf) {
      setFormValues(user)
      setLoading(false)
    } else {
      setLoading(false)
    }
    return () => setFormValues({})
  }, [employeeID])

  const handleSubmit = async (event) => {
    event.preventDefault()
    const updates = { ...formValues }
    if (employeeID) {
      delete updates?.photograph
      API.put('/employee', { updates }).then((res) => {
        if (res.data?.error === 'EmailDuplicate') {
          setEmailDup(true)
        } else if (res.data?.error === 'PinNumberDuplicate') {
          setPinNumberDuplicate(true)
        } else if (res.status === 200) {
          // Editing your own record: reload the signed-in user so the new
          // clinical role / teams apply without a full page refresh.
          String(employeeID) === String(user?._id) && getUserAccount()
          onClose ? onClose() : setToast({ message: 'Employee Updated' })
        }
      })
    } else if (isNew) {
      API.post('/employees/new', updates).then((res) => {
        if (res.status === 201) {
          navigate(`/employees/${res.data._id}`, { replace: true })
        } else if (
          res.status === 400 &&
          res.data?.error === 'email' &&
          res.data?.errorCode === 'duplicate'
        ) {
          setEmailDup(true)
          setEmailDupPopup(true)
        } else if (res.data?.errorCode === 'baseLocationRequired') {
          setBaseLocationError(res.data.error)
        }
      })
    } else if (isSelf) {
      // UPDATE OWN ACCOUNT
      // TODO
    }
  }

  if (loading) return <LinearProgress />

  if (notAllowed) {
    return (
      <Alert severity="error">You are not authorised to view this page.</Alert>
    )
  }

  if (limitReached && isNew)
    return (
      <UpgradeRequired
        open
        requiredTier={
          subscriptionTier === 1
            ? 'Premium or Enterprise'
            : subscriptionTier === 2
              ? 'Enterprise'
              : subscriptionTier === 3 && 'Additional Employees'
        }
        title="Employee Limit Reached"
        customMessage={`Your current subscription allows ${limitInfo.limit} active employees and your organisation already has ${limitInfo.used} active employees`}
        customHint={
          subscriptionTier === 3
            ? 'You can purchase additional employees using the link below'
            : 'Upgrade your subscription to increase your employee limit'
        }
        benefits={
          subscriptionTier === 1
            ? [
                `Premium Subscription: ${premiumEmployees} Active Employees`,
                `Enterprise Subscription: ${enterpriseEmployees} Active Employees`,
              ]
            : subscriptionTier === 2
              ? [
                  `Enterprise Subscription: ${enterpriseEmployees} Active Employees`,
                ]
              : null
        }
        goBack
        upgradeButtonText="Add Additional Employees"
      />
    )

  return (
    <EmployeeFormContext.Provider value={{ formValues, setFormValues }}>
      <Stack gap={1} component="form" onSubmit={handleSubmit}>
        {isNew && <Typography variant="title2">Add a New Employee</Typography>}
        <Box component={Paper} padding={1}>
          <AnDateField
            date={formValues?.employee?.employmentStartDate ?? null}
            onChange={(newDate) =>
              setFormValues((prev) => ({
                ...prev,
                employee: { ...prev.employee, employmentStartDate: newDate },
              }))
            }
            fullWidth
            label="Employment Start Date"
          />
          {isNew && (
            <Box mt={2}>
              <EmployeeBaseLocationsSelect
                value={formValues?.permittedBaseLocations ?? []}
                onChange={(ids) => {
                  setBaseLocationError(null)
                  setFormValues((prev) => ({
                    ...prev,
                    permittedBaseLocations: ids,
                  }))
                }}
                error={!!baseLocationError}
                helperText={
                  baseLocationError ??
                  'The base location(s) this employee works from'
                }
              />
            </Box>
          )}
        </Box>
        <PersonalDetails
          formValues={formValues}
          setFormValues={setFormValues}
          isSelf={isSelf}
          isNew={isNew}
          onImageChange={onImageChange}
        />
        <Address formValues={formValues} setFormValues={setFormValues} />
        <ContactInfo
          emailDup={emailDup}
          setEmailDup={setEmailDup}
          formValues={formValues}
          setFormValues={setFormValues}
        />
        <NextOfKin formValues={formValues} setFormValues={setFormValues} />
        <ProfessionalQuals />
        <ControlledDrugs />
        <DrivingLicence />
        {/* TODO {isNew && <UserAccount />} */}
        <Button
          type="submit"
          variant="contained"
          size="large"
          color="third"
          disabled={emailDup || pinNumberDuplicate}
        >
          Submit
        </Button>
        <ErrorModal
          open={emailDupPopup || pinNumberDuplicate}
          onClose={() => {
            setEmailDupPopup(false)
            setPinNumberDuplicate(false)
          }}
          message={
            emailDupPopup
              ? 'A user with this Email Address already exists'
              : pinNumberDuplicate
                ? 'A user with this PIN number already exists'
                : ''
          }
        />
      </Stack>
    </EmployeeFormContext.Provider>
  )
}

EmployeeForm.propTypes = {
  isSelf: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default EmployeeForm
